// Filter controls declared by the dashboard schema, rendered as one compact row in the top bar.
// Date range first, presets as rows, custom range behind a hairline; secondary actions live in
// the page's View menu (ViewMenu) so the bar stays quiet.
import { useEffect, useRef, useState } from 'react';
import type { FilterDef, Shell } from '../app/types';
import { Icon } from '../ui/Icon';
import { formatDate } from '../lib/format';

const PRESET_LABEL: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: 'Last 7 days',
  last_14_days: 'Last 14 days',
  month_to_date: 'Month to date',
  last_30_days: 'Last 30 days',
  last_month: 'Last month',
  last_90_days: 'Last 90 days',
  last_12_months: 'Last 12 months',
  year_to_date: 'Year to date',
  all_time: 'All time',
};

export type FilterValues = Record<string, unknown>;

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return { open, setOpen, ref };
}

export function rangeLabel(v: unknown, labels?: Record<string, string>): string {
  if (typeof v === 'string') return labels?.[v] ?? PRESET_LABEL[v] ?? v;
  if (v && typeof v === 'object' && (v as any).start) return `${formatDate((v as any).start)} – ${formatDate((v as any).end ?? (v as any).start)}`;
  return 'Date range';
}

function DateRange({ f, value, onChange, boundary }: { f: FilterDef; value: unknown; onChange: (v: unknown) => void; boundary: string }) {
  const { open, setOpen, ref } = usePopover();
  const custom = typeof value === 'object' && value ? (value as { start: string; end: string }) : null;
  const [start, setStart] = useState(custom?.start ?? '');
  const [end, setEnd] = useState(custom?.end ?? '');
  return (
    <div className="popover-anchor" ref={ref}>
      <button
        className="btn"
        aria-label={`${f.label}: ${rangeLabel(value, f.presetLabels)}`}
        title={value === f.default && f.defaultReason ? f.defaultReason : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="calendar" size={14} /> {rangeLabel(value, f.presetLabels)} <Icon name="chevronDown" size={12} className="chev" />
      </button>
      {open && (
        <div className="popover right" role="listbox" aria-label={f.label}>
          {(f.presets ?? Object.keys(PRESET_LABEL)).map((p) => (
            <button
              key={p}
              className="option"
              role="option"
              aria-selected={value === p}
              onClick={() => {
                onChange(p);
                setOpen(false);
              }}
            >
              <span className="check">{value === p ? '✓' : ''}</span>
              {f.presetLabels?.[p] ?? PRESET_LABEL[p] ?? p}
            </button>
          ))}
          {f.allowCustom && (
            <div className="popover-foot">
              <span className="hint">Custom range (UTC)</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="input" type="date" aria-label="Start date" min={boundary.slice(0, 10)} value={start} onChange={(e) => setStart(e.target.value)} />
                <input className="input" type="date" aria-label="End date" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
              <button
                className="btn sm primary"
                disabled={!start}
                onClick={() => {
                  onChange({ preset: 'custom', start, end: end || start });
                  setOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          )}
          <div className="popover-foot hint">Data available from {formatDate(boundary)}</div>
        </div>
      )}
    </div>
  );
}

function StoreSelect({ f, value, onChange, shell }: { f: FilterDef; value: unknown; onChange: (v: unknown) => void; shell: Shell }) {
  const { open, setOpen, ref } = usePopover();
  if (!f.multiple) {
    return (
      <label className="filter-select">
        <span>{f.label}</span>
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {shell.stores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.currency})
            </option>
          ))}
        </select>
      </label>
    );
  }
  const selected: string[] = value === 'all' || !Array.isArray(value) ? shell.stores.map((s) => s.id) : (value as string[]);
  const all = selected.length === shell.stores.length;
  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next.length === shell.stores.length || next.length === 0 ? 'all' : next);
  };
  const text = all ? 'All stores' : selected.length === 1 ? shell.stores.find((s) => s.id === selected[0])?.name : `${selected.length} stores`;
  return (
    <div className="popover-anchor" ref={ref}>
      <button className="btn" aria-label={`${f.label}: ${text}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="store" size={14} /> {text} <Icon name="chevronDown" size={12} className="chev" />
      </button>
      {open && (
        <div className="popover right" role="listbox" aria-multiselectable="true" aria-label={f.label}>
          <button className="option" onClick={() => onChange('all')}>
            <span className="check">{all ? '✓' : ''}</span>All stores
          </button>
          {shell.stores.map((s) => (
            <button key={s.id} className="option" role="option" aria-selected={!all && selected.includes(s.id)} onClick={() => toggle(s.id)}>
              <span className="check">{!all && selected.includes(s.id) ? '✓' : ''}</span>
              {s.name} <span className="muted" style={{ marginLeft: 'auto' }}>{s.currency}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterBar({ filters, values, onChange, shell }: {
  filters: FilterDef[];
  values: FilterValues;
  onChange: (id: string, v: unknown) => void;
  shell: Shell;
}) {
  // Store pickers first, then selects, date range last (closest to the eye's resting point on the right).
  const order = (f: FilterDef) => (f.type === 'store-select' ? 0 : f.type === 'select' ? 1 : 2);
  const ordered = [...filters].sort((a, b) => order(a) - order(b));
  return (
    <div className="filters" role="toolbar" aria-label="Filters">
      {ordered.map((f) => {
        if (f.type === 'date-range') return <DateRange key={f.id} f={f} value={values[f.id]} onChange={(v) => onChange(f.id, v)} boundary={shell.syncBoundary.startDate} />;
        if (f.type === 'store-select') return <StoreSelect key={f.id} f={f} value={values[f.id]} onChange={(v) => onChange(f.id, v)} shell={shell} />;
        return (
          <label className="filter-select" key={f.id}>
            <span>{f.label}</span>
            <select value={String(values[f.id] ?? '')} onChange={(e) => onChange(f.id, e.target.value)}>
              {(f.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}
